import React, { useEffect, useState, useCallback } from 'react';
import { ui, IRootScopeService } from 'angular';
import { getService } from '@Client/runner.hooks/useService';
import { IMenuItem } from './sidemenu.service';
import {
  labelType,
  linkType,
  linkWithActionCountType,
  linkWithActionType,
  linkWithCountType
} from './sidemenu.constants';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

type SidemenuItemProps = {
  item: IMenuItem;
  isMobile?: boolean;
  onMobileClose?: () => void;
  [k: string]: unknown;
};

export const SidemenuItem = (props: SidemenuItemProps) => {
  const { item, isMobile, onMobileClose } = props;
  const $state = getService('$state');
  const $rootScope = getService('$rootScope') as IRootScopeService;
  const pubsubService = getService('pubsubService');
  const [isActive, setIsActive] = useState(false);
  const [countTrigger, setCountTrigger] = useState(0);

  const handleNavigate = useCallback(() => {
    if (!item.target || !$state) return;

    const currentStateName = $state.current.name;
    if (item.target === currentStateName) return;

    if (item.data) {
      $state.go(item.target, item.data);
    } else {
      $state.go(item.target);
    }
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  }, [item, $state, isMobile, onMobileClose]);

  const handleAction = useCallback(() => {
    if (item.action) {
      item.action(item.param);
    }
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  }, [item, isMobile, onMobileClose]);

  const checkHighlight = useCallback(
    (item: IMenuItem, $state: ui.IStateService) => {
      if (!item.target) {
        setIsActive(false);
        return;
      }

      const targetState = item.target;
      let normalizedCurrentState = $state.current.name;

      if (normalizedCurrentState === STATE_MAPPINGS.report) {
        normalizedCurrentState = STATE_MAPPINGS.reports;
      }

      if (
        normalizedCurrentState &&
        normalizedCurrentState.indexOf('app.runner.setup.') === 0
      ) {
        normalizedCurrentState = 'app.runner.setup.';
      }

      if (
        normalizedCurrentState &&
        normalizedCurrentState.indexOf(`${STATE_MAPPINGS.library}.`) === 0
      ) {
        normalizedCurrentState = `${STATE_MAPPINGS.library}.`;
      }

      const stateMatches =
        targetState === normalizedCurrentState ||
        (normalizedCurrentState.endsWith('.') &&
          targetState.startsWith(normalizedCurrentState));

      if (item.data != undefined) {
        const paramsMatch =
          JSON.stringify(item.data) === JSON.stringify($state.params);
        setIsActive(stateMatches && paramsMatch);
      } else {
        setIsActive(stateMatches);
      }
    },
    []
  );

  useEffect(() => {
    if (!$state || !item.target) return;

    const updateHighlight = () => {
      checkHighlight(item, $state);
    };

    updateHighlight();

    const deregister = $rootScope?.$on('$stateChangeSuccess', updateHighlight);

    return () => {
      if (deregister) {
        deregister();
      }
    };
  }, [item, $state, $rootScope, checkHighlight]);

  useEffect(() => {
    if (!item.count) return;

    const SUBSCRIBER_ID = `sidemenu.item.${item.name}`;
    const forceUpdate = () => setCountTrigger((prev) => prev + 1);

    pubsubService.subscribe(
      'SIDEMENU_COUNT_UPDATED',
      forceUpdate,
      SUBSCRIBER_ID
    );

    return () => {
      pubsubService.unsubscribeAll(SUBSCRIBER_ID);
    };
  }, [pubsubService, item.count, item.name]);

  const renderTemplate = () => {
    switch (item.type) {
      case labelType:
        return <span>{item.name}</span>;

      case linkType:
        return (
          <div
            onClick={handleNavigate}
            className={`side-menu__item__link`}
            data-testid={`sidemenu-link-${item.name
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
          >
            <i className={item.icon} title={item.hovertext}></i>
            {isMobile ? <span>{item.mobilename}</span> : <div>{item.name}</div>}
          </div>
        );

      case linkWithActionType:
        return (
          <div
            onClick={handleAction}
            className={`side-menu__item__link`}
            data-testid={`sidemenu-action-${item.name
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
          >
            <i className={item.icon}></i>
            {item.name}
          </div>
        );

      case linkWithCountType: {
        const count = item.count?.();
        const shouldShow = item.show ? item.show() : true;
        // countTrigger is used to force re-render when count changes
        void countTrigger;

        if (isMobile) {
          return (
            <div
              onClick={handleNavigate}
              className={`side-menu__item__link`}
              data-testid={`sidemenu-count-${item.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <i className={item.icon}></i>
              {item.name}
              {shouldShow && count !== null && count !== undefined && (
                <span className="badge">{count}</span>
              )}
            </div>
          );
        } else {
          return shouldShow ? (
            <div
              onClick={handleNavigate}
              className={`side-menu__item__link badge-todo-desktop ${
                count == null ? 'hide-badge' : ''
              }`}
              data-badge={count?.toString()}
              data-testid={`sidemenu-count-${item.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <i className={item.icon}></i>
              <div>{item.name}</div>
            </div>
          ) : null;
        }
      }

      case linkWithActionCountType: {
        const actionCount = item.count?.();
        // countTrigger is used to force re-render when count changes
        void countTrigger;

        if (isMobile) {
          const showActionCount = item.show ? item.show() : true;
          return (
            <div
              onClick={handleAction}
              data-badge={actionCount?.toString()}
              className={`side-menu__item__link`}
              data-testid={`sidemenu-action-count-${item.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <i className={item.icon}></i>
              {item.name}
              {showActionCount &&
                actionCount !== null &&
                actionCount !== undefined && (
                  <span className="badge">{actionCount}</span>
                )}
            </div>
          );
        } else {
          return (
            <div
              onClick={handleAction}
              data-badge={actionCount?.toString()}
              className={`side-menu__item__link badge-todo-desktop`}
              data-testid={`sidemenu-action-count-${item.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <i className={item.icon}></i>
              {item.name}
            </div>
          );
        }
      }

      default:
        return null;
    }
  };

  return (
    <div
      className={`side-menu__item ${item.type === labelType ? labelType : ''} ${
        isActive ? 'active' : ''
      }`}
      title={item.hovertext}
    >
      {renderTemplate()}
    </div>
  );
};
